import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';



import { useFormState } from '@/hooks/useFormState';
import { useSocialLogin } from '@/hooks/useSocialLogin';
import { useVerificationCode } from '@/hooks/useVerificationCode';
import { emailRegister } from '@/services/authService';
import { validateRegisterForm } from '@/utils/authValidation';



import { AuthFormLayout } from '../common/AuthFormLayout';
import { Button } from '../common/Button';
import { FormInput } from '../common/FormInput';
import { TermsCheckbox } from '../common/TermsCheckbox';
import { VerificationCodeInput } from '../common/VerificationCodeInput';


interface RegisterFormData {
  email: string;
  verificationCode: string;
  password: string;
  terms: boolean;
}

// 获取query ticket并传递
const urlParams = new URLSearchParams(window.location.search);
const ticket = urlParams.get('ticket') || undefined;
const product = urlParams.get('product') || undefined;

export const RegisterForm = () => {
  const { t } = useTranslation();
  const { handleGithubLogin, handleGoogleLogin } = useSocialLogin();
  const {
    isCodeSent,
    countdown,
    isLoading: codeLoading,
    sendCode,
    cleanup,
  } = useVerificationCode();

  const { values, errors, isLoading, setValue, setError, clearError, setLoading } =
    useFormState<RegisterFormData>({
      initialValues: {
        email: '',
        verificationCode: '',
        password: '',
        terms: true,
      },
      validate: validateRegisterForm,
    });

  // 清理定时器
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // 发送验证码
  const handleSendCode = async () => {
    const result = await sendCode(values.email);
    if (!result.success && result.error) {
      setError('email', result.error);
    } else {
      clearError('email');
    }
  };

  // 处理邮箱注册
  const handleEmailSignUp = async () => {
    const validationErrors = validateRegisterForm(values);

    if (Object.keys(validationErrors).length > 0) {
      Object.entries(validationErrors).forEach(([field, error]) => {
        setError(field, error);
      });

      if (validationErrors.terms) {
        toast.error(t('registerForm.termsError'));
      }
      return;
    }

    try {
      setLoading(true);
      const response = await emailRegister({
        email: values.email,
        code: values.verificationCode,
        password: values.password,
        confirmPassword: values.password,
        auth_ticket_id: ticket,
        product,
      });

      if (response.status === 200) {
        // query 需要原封不动，路径改为 /login
        window.location.href = `/login${window.location.search}`;
      } else {
        toast.error(response.message || t('registerForm.registerError'));
      }
    } catch (error: any) {
      const errorMessage = error.message || t('registerForm.registerError');
      setError('general', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle social login with loading state
  const handleSocialLogin = async (loginFn: (ticket?: string, product?: string) => void) => {
    try {
      setLoading(true);
      loginFn(ticket, product);
    } catch (_error) {
      setLoading(false);
    }
  };

  return (
    <AuthFormLayout
      onGithubLogin={() => handleSocialLogin(handleGithubLogin)}
      onGoogleLogin={() => handleSocialLogin(handleGoogleLogin)}
      isLoading={isLoading}
      generalError={errors.general}
      dividerText={t('registerForm.dividerText')}
      googleText={t('registerForm.googleText')}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleEmailSignUp();
        }}
      >
        {/* Email input */}
        <FormInput
          type="email"
          name="email"
          id="register-email"
          autoComplete="email"
          value={values.email}
          tabIndex={1}
          onChange={(e) => {
            setValue('email', e.target.value);
            clearError('email');
            clearError('general');
          }}
          placeholder={t('registerForm.emailPlaceholder')}
          error={errors.email}
        />

        {/* Verification code input */}
        <VerificationCodeInput
          value={values.verificationCode}
          onChange={(value) => {
            setValue('verificationCode', value);
            clearError('verificationCode');
            clearError('general');
          }}
          onSendCode={handleSendCode}
          error={errors.verificationCode}
          isCodeSent={isCodeSent}
          countdown={countdown}
          isLoading={codeLoading}
          disabled={!values.email}
          tabIndex={2}
        />

        {/* Password input */}
        <FormInput
          type="password"
          name="newPassword"
          id="register-password"
          autoComplete="new-password"
          value={values.password}
          onChange={(e) => {
            setValue('password', e.target.value);
            clearError('password');
            clearError('general');
          }}
          placeholder={t('registerForm.passwordPlaceholder')}
          error={errors.password}
          tabIndex={3}
        />

        {/* Terms agreement and register button */}
        <div className="flex flex-col gap-[8px]">
          <TermsCheckbox
            checked={values.terms}
            onChange={(e) => {
              setValue('terms', e.target.checked);
              clearError('terms');
            }}
            error={errors.terms}
          />

          <Button
            onClick={handleEmailSignUp}
            disabled={isLoading}
            isLoading={isLoading}
            tabIndex={4}
          >
            {t('registerForm.registerButton')}
          </Button>
        </div>

        {/* Bottom links */}
        <div className="mt-[20px] flex justify-center">
          <Link
            to="/login"
            className="auth-link text-[12px] font-medium text-black hover:text-[#326BFB]"
          >
            {t('registerForm.haveAccount')}
          </Link>
        </div>
      </form>
    </AuthFormLayout>
  );
};